import { Capacitor } from '@capacitor/core';
import type { AppEnvironment, RuntimeDevConfig } from './types';
import { env as devEnv } from './dev';
import { env as ip4xEnv } from './ip4x';
import { env as ip4cmsEnv } from './ip4cms';
import { env as ip4cmsStagingEnv } from './ip4cms-staging';
import { env as sbmdcStagingEnv } from './sbmdc-staging';
import { env as stellmunidcStagingEnv } from './stellmunidc-staging';
import { env as fadtStagingEnv } from './fadt-staging';
import { env as sbmEnv } from './sbm';
import { env as sbmLocalEnv } from './sbm-local';
import { env as ip4xRootEnv } from './ip4x-root';
import { env as hire24Env } from './hire24';
import { env as prodEnv } from './prod';

export type {
  AppEnvironment,
  MemberPortalAppShell,
  AuthMode,
  PageHeroCopy,
  ResolvedPageHeroCopy,
  ResidentAppPageCopy,
} from './types';
export type { ResolvedPageCopy } from './page-copy-defaults';

declare const __APP_MODE__: string;
declare const __GIT_COMMIT_SHA__: string;

declare global {
  interface Window {
    __IP4X_DEV__?: RuntimeDevConfig;
  }
}

const ENV_MAP: Record<string, AppEnvironment> = {
  dev: devEnv,
  ip4x: ip4xEnv,
  ip4cms: ip4cmsEnv,
  'ip4cms-staging': ip4cmsStagingEnv,
  'sbmdc-staging': sbmdcStagingEnv,
  'stellmunidc-staging': stellmunidcStagingEnv,
  'fadt-staging': fadtStagingEnv,
  sbm: sbmEnv,
  'sbm-local': sbmLocalEnv,
  'ip4x-root': ip4xRootEnv,
  hire24: hire24Env,
  prod: prodEnv,
};

/**
 * The build mode is fixed at compile time via Vite `--mode`. We expose
 * it through {@link __APP_MODE__} (define-injected) so this resolves at
 * import time without a `Symbol` lookup or async hook.
 */
function resolveMode(): string {
  if (typeof __APP_MODE__ !== 'undefined' && __APP_MODE__) return __APP_MODE__;
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) return import.meta.env.MODE;
  return 'dev';
}

const mode = resolveMode();
const resolved = ENV_MAP[mode];

if (!resolved) {
  // Misconfiguration is caught at build time normally (Vite ensures mode is set).
  // Falling back to dev keeps localhost demos alive while warning loudly.
  console.warn(`[env] Unknown build mode "${mode}", falling back to dev`);
}

const baseEnv = resolved ?? devEnv;

function readRuntimeApiOverrides(): RuntimeDevConfig {
  if (typeof window === 'undefined') return {};
  return window.__IP4X_DEV__ ?? {};
}

function rewriteLocalhostForNative(url: string): string {
  if (!url || !Capacitor.isNativePlatform()) return url;

  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return url;
    }

    if (Capacitor.getPlatform() === 'android') {
      parsed.hostname = '10.0.2.2';
    }

    return parsed.toString();
  } catch {
    return url;
  }
}

const runtimeOverrides = readRuntimeApiOverrides();
const apiBaseUrl = rewriteLocalhostForNative(runtimeOverrides.apiBaseUrl?.trim() || baseEnv.apiBaseUrl);
const coreApiUrl = rewriteLocalhostForNative(runtimeOverrides.coreApiUrl?.trim() || baseEnv.coreApiUrl);

/**
 * Resolve the build's app-template pin. This is OPTIONAL: when a build does not
 * pin a template (empty value) the app must still boot and load the default
 * domain's branding/config — the backend then resolves the tenant's effective
 * member template on its own. A hard throw here ran at module-import time, before
 * any domain resolution, and white-screened every build with an empty value
 * (losing the base branding load). We therefore only *warn* and fall back to ''
 * rather than crashing; a malformed (non-empty) value is ignored the same way.
 */
function normalizeAppTemplateId(value: string, buildMode: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidPattern.test(normalized)) {
    console.warn(`[env] Ignoring invalid appTemplateId for resident-app build "${buildMode}": "${normalized}"`);
    return '';
  }

  return normalized;
}

export const env: AppEnvironment = {
  ...baseEnv,
  apiBaseUrl,
  coreApiUrl,
  appTemplateId: normalizeAppTemplateId(baseEnv.appTemplateId, baseEnv.buildMode),
  gitCommitSha:
    typeof __GIT_COMMIT_SHA__ !== 'undefined' && __GIT_COMMIT_SHA__
      ? __GIT_COMMIT_SHA__
      : undefined,
};
